import {
  type Decimal,
  decimalToString,
  fromScaled,
  isZeroDecimal,
  toSignedScaled,
  trimDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  pow10Big,
} from "@/lib/tool-kit/math/bigint";

/**
 * Rounding on exact decimals.
 *
 * A decimal typed as 1.005 is stored here as the whole number 1005 with scale 3, so
 * "does the dropped part equal one half?" is a comparison of integers, not of doubles.
 * That is the whole point: in binary floating point 1.005 is really 1.00499999999999989…,
 * which is why `(1.005).toFixed(2)` says "1.00" while every schoolbook says 1.01.
 *
 * Ten modes are supported. Six decide ties differently (the nearest value is the same
 * for all of them) and four are directed (they ignore which neighbour is nearer).
 */
export type RoundingMode =
  | "half-away"
  | "half-up"
  | "half-down"
  | "half-zero"
  | "half-even"
  | "half-odd"
  | "ceiling"
  | "floor"
  | "truncate"
  | "away";

export const ROUNDING_MODES: readonly RoundingMode[] = [
  "half-away",
  "half-up",
  "half-down",
  "half-zero",
  "half-even",
  "half-odd",
  "ceiling",
  "floor",
  "truncate",
  "away",
];

/** Where the dropped part sits relative to one half of the unit being kept. */
export type HalfRelation = "below" | "tie" | "above" | "exact";

export interface DivisionRounding {
  /** The rounded quotient, a signed whole number. */
  value: bigint;
  /** Magnitude of the quotient before rounding. */
  floorMagnitude: bigint;
  relation: HalfRelation;
}

/** Round num/den (den > 0) to a whole number under `mode`. */
export function roundDivision(
  num: bigint,
  den: bigint,
  mode: RoundingMode
): DivisionRounding {
  const negative = num < BIG_ZERO;
  const magnitude = negative ? -num : num;
  const q = magnitude / den;
  const rest = magnitude % den;
  if (rest === BIG_ZERO) {
    return { value: negative ? -q : q, floorMagnitude: q, relation: "exact" };
  }
  const doubled = rest * BIG_TWO;
  let relation: HalfRelation = "tie";
  if (doubled < den) {
    relation = "below";
  } else if (doubled > den) {
    relation = "above";
  }
  const up = roundsUp(mode, relation, negative, q);
  const result = up ? q + BIG_ONE : q;
  return {
    value: negative ? -result : result,
    floorMagnitude: q,
    relation,
  };
}

/** Does the magnitude go up by one? (Otherwise it stays at the truncated value.) */
function roundsUp(
  mode: RoundingMode,
  relation: HalfRelation,
  negative: boolean,
  q: bigint
): boolean {
  switch (mode) {
    case "truncate":
      return false;
    case "away":
      return true;
    case "ceiling":
      return !negative;
    case "floor":
      return negative;
    default:
      break;
  }
  if (relation === "below") {
    return false;
  }
  if (relation === "above") {
    return true;
  }
  return tieGoesUp(mode, negative, q);
}

function tieGoesUp(mode: RoundingMode, negative: boolean, q: bigint): boolean {
  switch (mode) {
    case "half-away":
      return true;
    case "half-zero":
      return false;
    case "half-up":
      return !negative;
    case "half-down":
      return negative;
    case "half-even":
      return q % BIG_TWO !== BIG_ZERO;
    default:
      return q % BIG_TWO === BIG_ZERO;
  }
}

const TRAILING = /0+$/;

/* ------------------------------------------------------------- to a place */

export interface PlaceRounding {
  mode: RoundingMode;
  /** The rounded value. */
  result: Decimal;
  /** Digits kept (as typed, up to the rounding place) and digits dropped. */
  kept: string;
  dropped: string;
  /** The first dropped digit, or null when nothing is dropped. */
  nextDigit: string | null;
  relation: HalfRelation;
  /** Rounded minus original, exactly, as a decimal. */
  difference: Decimal;
  /** True when the input already had no digits beyond the place. */
  unchanged: boolean;
}

/**
 * Round to `places` decimal places (negative for tens, hundreds …).
 * Zero results never carry a minus sign.
 */
export function roundToPlace(
  value: Decimal,
  places: number,
  mode: RoundingMode
): PlaceRounding {
  const { value: signed, scale } = toSignedScaled(value);
  const drop = scale - places;
  const digits = value.int + value.frac;
  const split = digits.length - Math.max(drop, 0);
  if (drop <= 0) {
    // Nothing lies beyond the place: the value is already exact there.
    return {
      mode,
      result: padTo(value, places),
      kept: digits,
      dropped: "",
      nextDigit: null,
      relation: "exact",
      difference: fromScaled(BIG_ZERO, 0),
      unchanged: true,
    };
  }
  const rounding = roundDivision(signed, pow10Big(drop), mode);
  const resultScale = Math.max(places, 0);
  const shifted =
    places < 0 ? rounding.value * pow10Big(-places) : rounding.value;
  const result = fromScaled(shifted, resultScale);
  const difference = trimDecimal(
    fromScaled(shifted * pow10Big(scale - resultScale) - signed, scale)
  );
  return {
    mode,
    result,
    kept: digits.slice(0, Math.max(split, 0)),
    dropped: digits.slice(Math.max(split, 0)),
    nextDigit: digits.at(Math.max(split, 0)) ?? null,
    relation: rounding.relation,
    difference,
    unchanged: false,
  };
}

function padTo(value: Decimal, places: number): Decimal {
  if (places <= 0 || value.frac.length >= places) {
    return value;
  }
  return { ...value, frac: value.frac.padEnd(places, "0") };
}

/* -------------------------------------------------------- to an increment */

export interface IncrementRounding {
  mode: RoundingMode;
  result: Decimal;
  /** value ÷ increment, before rounding, as text. */
  ratio: string;
  multiple: bigint;
  relation: HalfRelation;
  difference: Decimal;
}

/** Round to the nearest multiple of `increment` (> 0): 2.37 to 0.05 is 2.35. */
export function roundToIncrement(
  value: Decimal,
  increment: Decimal,
  mode: RoundingMode
): IncrementRounding | null {
  if (isZeroDecimal(increment) || increment.negative) {
    return null;
  }
  const a = toSignedScaled(value);
  const b = toSignedScaled(increment);
  const scale = Math.max(a.scale, b.scale);
  const A = a.value * pow10Big(scale - a.scale);
  const B = b.value * pow10Big(scale - b.scale);
  const rounding = roundDivision(A, B, mode);
  const total = rounding.value * B;
  const result = fromScaled(total, scale);
  return {
    mode,
    result: trimDecimal(result),
    ratio: ratioText(A, B),
    multiple: rounding.value,
    relation: rounding.relation,
    difference: trimDecimal(fromScaled(total - A, scale)),
  };
}

function ratioText(a: bigint, b: bigint): string {
  const negative = a < BIG_ZERO;
  const magnitude = negative ? -a : a;
  const places = 6;
  const scaledNumerator = magnitude * pow10Big(places);
  const scaled = scaledNumerator / b;
  const exact = scaledNumerator % b === BIG_ZERO;
  const digits = scaled.toString().padStart(places + 1, "0");
  const cut = digits.length - places;
  const fraction = digits.slice(cut).replace(TRAILING, "");
  const body =
    fraction === ""
      ? digits.slice(0, cut)
      : `${digits.slice(0, cut)}.${fraction}`;
  return `${negative ? "−" : ""}${body}${exact ? "" : "…"}`;
}

/* ------------------------------------------------------ floating-point foil */

/**
 * What `Number(x).toFixed(places)` returns, and the exact binary value it was working
 * from — to show why floating point disagrees with decimal rounding. Null outside the
 * range `toFixed` accepts.
 */
export function floatingPointView(
  value: Decimal,
  places: number
): { toFixed: string; exactBinary: string } | null {
  const number = Number(decimalToString(value, false));
  if (!Number.isFinite(number) || places < 0 || places > 100) {
    return null;
  }
  const exact = number.toFixed(60).replace(TRAILING, "");
  return {
    toFixed: number.toFixed(places).replace("-", "−"),
    exactBinary: exact.endsWith(".") ? exact.slice(0, -1) : exact,
  };
}
